import React, { useState } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight, Award, Users } from 'lucide-react';
import { mockData } from '../mock';

const Testimonials = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  // Extended testimonials with more detailed stories
  const extendedTestimonials = [
    ...mockData.testimonials,
    {
      id: 4,
      name: "Arjun Patel",
      role: "Automotive Entrepreneur, Ahmedabad",
      content: "The comprehensive training at Unisol Global gave me not just technical skills but also the business knowledge to start my own detailing center. Within 6 months, I had a thriving business with repeat customers.",
      rating: 5,
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=387&q=80"
    },
    {
      id: 5,
      name: "Sarah Ahmed",
      role: "PPF Specialist, Hyderabad",
      content: "Nirmal sir's expertise in PPF installation is unmatched. The precision and attention to detail I learned from him has made me the go-to PPF specialist in my city. My customers always recommend me to others.",
      rating: 5,
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b1e5?ixlib=rb-4.0.3&auto=format&fit=crop&w=387&q=80"
    },
    {
      id: 6,
      name: "Vikram Singh",
      role: "Luxury Car Owner, Mumbai",
      content: "I've had my BMW and Mercedes serviced at Unisol Global multiple times. The quality of their PPF and ceramic coating work is exceptional. My cars always look showroom-new even after years of use.",
      rating: 5,
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=387&q=80"
    }
  ];

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % extendedTestimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + extendedTestimonials.length) % extendedTestimonials.length);
  };

  const stats = [
    { number: "100+", label: "Trained Professionals", icon: Users },
    { number: "25+", label: "Years Experience", icon: Award },
    { number: "500+", label: "Satisfied Customers", icon: Star },
    { number: "98%", label: "Success Rate", icon: Award }
  ];

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6">
            <h1 className="text-5xl md:text-6xl font-bold text-white">
              Success
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500"> Stories</span>
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Hear from our students and customers who have achieved excellence with Unisol Global
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div key={index} className="text-center space-y-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full mx-auto flex items-center justify-center">
                    <IconComponent className="text-slate-900" size={28} />
                  </div>
                  <div className="text-3xl md:text-4xl font-bold text-amber-400">{stat.number}</div>
                  <div className="text-slate-300 text-sm">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Video Testimonials Section */}
      <section className="py-20 bg-slate-900/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl font-bold text-white">
              Video
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500"> Testimonials</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Watch our customers share their experiences with Unisol Global
            </p>
          </div>

          <div className="flex justify-center">
            <div className="w-full max-w-4xl">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-700">
                <video 
                  controls
                  className="w-full h-auto aspect-video"
                  poster=""
                  preload="metadata"
                >
                  <source 
                    src="https://customer-assets.emergentagent.com/job_auto-care-site/artifacts/xhdnc1b0_IMG_4555.mp4" 
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
              </div>
              
              <div className="mt-6 text-center">
                <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-bold text-white mb-2">Customer Experience</h3>
                  <p className="text-slate-300">
                    Real feedback from our valued customers about their experience with Unisol Global's automotive detailing services
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Testimonial Carousel */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl font-bold text-white">
              What Our
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500"> Community Says</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Real stories from real people who trust Unisol Global
            </p>
          </div>

          <div className="relative">
            {/* Main Testimonial Card */}
            <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm rounded-3xl p-12 border border-slate-700 relative overflow-hidden">
              <div className="absolute top-8 left-8 w-16 h-16 bg-gradient-to-r from-amber-400/20 to-orange-500/20 rounded-full flex items-center justify-center">
                <Quote className="text-amber-400" size={32} />
              </div>
              
              <div className="text-center space-y-8 pt-8">
                <div className="flex justify-center mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className="w-6 h-6 text-amber-400 fill-current" 
                    />
                  ))}
                </div>
                
                <blockquote className="text-2xl md:text-3xl text-white leading-relaxed font-light max-w-4xl mx-auto">
                  "{extendedTestimonials[currentTestimonial].content}"
                </blockquote>
                
                <div className="flex items-center justify-center space-x-6 pt-8">
                  <div className="w-16 h-16 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                    <span className="text-slate-900 font-bold text-xl">
                      {extendedTestimonials[currentTestimonial].name.charAt(0)}
                    </span>
                  </div>
                  <div className="text-left">
                    <div className="text-xl font-bold text-white">
                      {extendedTestimonials[currentTestimonial].name}
                    </div>
                    <div className="text-amber-400 font-semibold">
                      {extendedTestimonials[currentTestimonial].role}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={prevTestimonial}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-slate-800/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-slate-700 transition-all duration-200"
            >
              <ChevronLeft className="text-white" size={24} />
            </button>
            
            <button
              onClick={nextTestimonial}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-slate-800/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-slate-700 transition-all duration-200"
            >
              <ChevronRight className="text-white" size={24} />
            </button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center space-x-3 mt-8">
            {extendedTestimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentTestimonial(index)}
                className={`w-3 h-3 rounded-full transition-all duration-200 ${
                  index === currentTestimonial
                    ? 'bg-amber-400 scale-125'
                    : 'bg-slate-600 hover:bg-slate-500'
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* All Testimonials Grid */}
      <section className="py-20 bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl font-bold text-white">
              More
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500"> Success Stories</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {extendedTestimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-slate-900/50 rounded-xl p-6 border border-slate-700 hover:border-amber-500/50 transition-all duration-300 hover:-translate-y-2">
                <div className="space-y-4">
                  <div className="flex justify-between items-start">
                    <div className="flex">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-amber-400 fill-current" />
                      ))}
                    </div>
                    <Quote className="text-amber-400/30" size={24} />
                  </div>
                  
                  <p className="text-slate-300 text-sm leading-relaxed italic">
                    "{testimonial.content}"
                  </p>
                  
                  <div className="flex items-center space-x-3 pt-4 border-t border-slate-700">
                    <div className="w-10 h-10 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                      <span className="text-slate-900 font-bold text-sm">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <div className="text-white font-semibold text-sm">{testimonial.name}</div>
                      <div className="text-slate-400 text-xs">{testimonial.role}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-amber-500/10 to-orange-500/10 backdrop-blur-sm border-y border-amber-500/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 className="text-4xl font-bold text-white">
            Ready to Write
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500"> Your Success Story?</span>
          </h2>
          <p className="text-xl text-slate-300 leading-relaxed">
            Join hundreds of successful professionals and satisfied customers who chose Unisol Global for excellence
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/training"
              className="bg-gradient-to-r from-amber-500 to-orange-500 text-slate-900 px-8 py-4 rounded-lg font-bold text-lg hover:from-amber-400 hover:to-orange-400 transition-all duration-200 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
            >
              Start Your Training
            </a>
            <a
              href="/contact"
              className="border-2 border-amber-400 text-amber-400 px-8 py-4 rounded-lg font-bold text-lg hover:bg-amber-400 hover:text-slate-900 transition-all duration-200"
            >
              Get Your Service Quote
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Testimonials;